import { Session } from '../types/session';
import {
  Customer,
  CustomerCreditSummary,
  CustomerDeliveryScheduleParams,
  CustomerDeliveryScheduleResponse,
  CustomerNotification,
  CustomerNotificationOptions,
  CustomerNotificationTemplate,
  CustomerNotificationType,
  CustomerPortalAccessResponse,
  Delivery,
  GetCreditSummaryOptions,
  GetCustomerOptions,
  UpdateCustomerRequest,
} from '../types/customer';
import { rechargeApiRequest } from '../utils/request';

export async function getCustomer(session: Session, options?: GetCustomerOptions): Promise<Customer> {
  const id = session.customerId;
  if (!id) {
    throw new Error('Not logged in.');
  }
  const { customer } = await rechargeApiRequest<{ customer: Customer }>(
    'get',
    `/customers`,
    {
      id,
      query: { include: options?.include },
    },
    session
  );
  return customer;
}

export async function updateCustomer(session: Session, updateRequest: UpdateCustomerRequest): Promise<Customer> {
  const id = session.customerId;
  if (!id) {
    throw new Error('Not logged in.');
  }
  const { customer } = await rechargeApiRequest<{ customer: Customer }>(
    'put',
    `/customers`,
    { id, data: updateRequest },
    session
  );
  return customer;
}

export async function getDeliverySchedule(
  session: Session,
  query?: CustomerDeliveryScheduleParams
): Promise<Delivery[]> {
  const id = session.customerId;
  if (!id) {
    throw new Error('Not logged in.');
  }
  const { deliveries } = await rechargeApiRequest<CustomerDeliveryScheduleResponse>(
    'get',
    `/customers/${id}/delivery_schedule`,
    { query },
    session
  );
  return deliveries;
}

export async function getCustomerPortalAccess(session: Session): Promise<CustomerPortalAccessResponse> {
  return rechargeApiRequest<CustomerPortalAccessResponse>('get', '/portal_access', {}, session);
}

export async function getActiveChurnLandingPageURL(
  session: Session,
  subscriptionId: string | number,
  redirectURL: string
): Promise<string> {
  const { base_url, customer_hash, temp_token } = await getCustomerPortalAccess(session);
  return `${base_url.replace(
    'portal',
    'pages'
  )}${customer_hash}/subscriptions/${subscriptionId}/cancel?token=${temp_token}&subscription=${subscriptionId}&redirect_to=${redirectURL}`;
}

export async function getGiftRedemptionLandingPageURL(
  session: Session,
  giftId: string | number,
  redirectURL: string
): Promise<string> {
  const { base_url, customer_hash, temp_token } = await getCustomerPortalAccess(session);
  return `${base_url.replace(
    'portal',
    'pages'
  )}${customer_hash}/gifts/${giftId}?token=${temp_token}&redirect_to=${redirectURL}`;
}

const customerNotificationMap: Record<
  CustomerNotification,
  { type: CustomerNotificationType; template_type: CustomerNotificationTemplate }
> = {
  SHOPIFY_UPDATE_PAYMENT_INFO: {
    type: 'email',
    template_type: 'shopify_update_payment_information',
  },
};

export async function sendCustomerNotification<T extends CustomerNotification>(
  session: Session,
  notification: CustomerNotification,
  options?: CustomerNotificationOptions<T>
): Promise<void> {
  const customerId = session.customerId;
  if (!customerId) {
    throw new Error('Not logged in.');
  }
  const data = customerNotificationMap[notification];
  if (!data) {
    throw new Error('Notification not supported.');
  }
  return rechargeApiRequest<void>(
    'post',
    `/customers/${customerId}/notifications`,
    {
      data: {
        ...data,
        template_vars: options,
      },
    },
    session
  );
}

export async function getCreditSummary(
  session: Session,
  options?: GetCreditSummaryOptions
): Promise<CustomerCreditSummary> {
  const id = session.customerId;
  if (!id) {
    throw new Error('Not logged in.');
  }
  const { credit_summary } = await rechargeApiRequest<{ credit_summary: CustomerCreditSummary }>(
    'get',
    `/customers/${id}/credit_summary`,
    {
      query: { include: options?.include },
    },
    session
  );
  return credit_summary;
}
